// @ts-nocheck
/**
 * Sentry setup for a Shopify theme — portable across theme repos.
 *
 * @version 1.0.0 (2026-06-11)
 *
 * Load this (deferred) immediately after the Sentry loader script:
 *
 *   <script src="https://js.sentry-cdn.com/<key>.min.js" crossorigin="anonymous" defer></script>
 *   <script src="{{ 'setup-sentry.js' | asset_url }}" defer></script>
 *
 * It is self-contained — store and theme context are read from Shopify's
 * injected `window.Shopify` global, so no inline Liquid is required.
 *
 * Responsibilities:
 *   1. Prefix every Sentry event with PREFIX so events stay distinguishable
 *      when multiple stores/themes report into one Sentry project.
 *   2. Scan the rendered DOM for Shopify "Translation missing: <key>" strings
 *      and report each unique key as a warning.
 *
 * To reuse in another theme repo: copy this file and change PREFIX below.
 */
(function () {
  'use strict';

  // --- Per-repo config -------------------------------------------------------
  var PREFIX = 'Bo+Tee';

  // Case-insensitive (/i): Shopify renders missing keys as "Translation missing:"
  // with a capital T, but we match defensively in case the casing ever changes.
  var TRANSLATION_PATTERN = /translation missing:\s*([^\s"'<>]+)/i;

  // Store + theme context from Shopify's injected storefront globals.
  var shopify = window.Shopify || {};
  var STORE = shopify.shop || 'unknown'; // e.g. "bo-and-tee.myshopify.com"
  var THEME = (shopify.theme && shopify.theme.name) || 'unknown';
  // Don't report from unpublished/dev themes (theme role is "development").
  var IS_DEV_THEME = Boolean(shopify.theme && shopify.theme.role === 'development');
  var COUNTRY = (shopify.country || 'unknown').toLowerCase();
  var LOCALE = (shopify.locale || 'unknown').toLowerCase();

  // 1. Prefix every event -----------------------------------------------------
  function configure() {
    Sentry.init({
      beforeSend: function (event) {
        if (IS_DEV_THEME) return null; // drop all events on development themes
        if (event.message) {
          event.message = PREFIX + ': ' + event.message;
        }
        var values = event.exception && event.exception.values;
        if (values && values[0] && values[0].value) {
          values[0].value = PREFIX + ': ' + values[0].value;
        }
        // Stamp store/theme/i18n context on every event so all issues —
        // not just missing-translation warnings — carry the same tags.
        event.tags = event.tags || {};
        event.tags['theme.name'] = THEME;
        event.tags['store.name'] = STORE;
        event.tags['i18n.country'] = COUNTRY;
        event.tags['i18n.locale'] = LOCALE;
        event.tags['i18n.lang-country'] = LOCALE + '-' + COUNTRY;
        return event;
      }
    });
  }

  // 2. Report missing translations -------------------------------------------
  function scan() {
    if (!window.Sentry || !document.body) return;

    var walker = document.createTreeWalker(
      document.body,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: function (node) {
          var parent = node.parentNode;
          if (parent) {
            var tag = parent.nodeName;
            if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'NOSCRIPT') {
              return NodeFilter.FILTER_REJECT;
            }
          }
          return TRANSLATION_PATTERN.test(node.nodeValue)
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_REJECT;
        }
      }
    );

    var seen = {};
    var keys = [];
    var node;
    while ((node = walker.nextNode())) {
      var match = node.nodeValue.match(TRANSLATION_PATTERN);
      if (!match) continue;
      var key = match[1];
      if (seen[key]) continue; // dedupe within the page
      seen[key] = true;
      keys.push(key);
    }

    // Exposed on window (same references) so the matched keys can be inspected
    // from the console for debugging (e.g. window.__translationMissing.keys).
    window.__translationMissing = { seen: seen, keys: keys };

    if (!keys.length) return;

    var path = location.pathname;

    Sentry.onLoad(function () {
      keys.forEach(function (key) {
        Sentry.withScope(function (scope) {
          scope.setLevel('warning'); // a missing string isn't a crash
          scope.setTag('i18n.key', key);
          scope.setFingerprint(['i18n-missing', key]); // one issue per key
          scope.setExtras({ path: path });
          Sentry.captureMessage('Missing translation: ' + key);
        });
      });
    });
  }

  function scheduleScan() {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(scan, { timeout: 3000 });
    } else {
      setTimeout(scan, 1000);
    }
  }

  // This script is deferred and ordered after the Sentry loader, so by the time
  // it runs the loader has executed and window.Sentry exists. The readyState
  // guard keeps it correct even if the file is loaded some other way.
  function start() {
    if (window.Sentry && Sentry.onLoad) Sentry.onLoad(configure);
    scheduleScan();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
